import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { Chip } from "@mui/material";
import { ISO639Lang } from "../../constans/languagesList";
import {
  LocalizedText,
  Quiz,
  QuizEvaluationType,
  QuizQuestion,
  QuizQuestionType,
} from "../../types/Quiz";
import {
  evaluationTypeLabels,
  getRetakeSummary,
  isFeedbackQuizType,
  quizTypeLabels,
} from "./systemQuizModel";
import s from "./SystemQuizzes.module.css";
import { getQuizQuestions, hasQuizChapters } from "../../utils/quizChapters";

type SystemQuizViewProps = {
  quiz: Quiz;
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

const addLanguages = (
  languages: Set<string>,
  value: LocalizedText[] | undefined,
) => value?.forEach((item) => languages.add(item.language));

const getQuizLanguages = (quiz: Quiz, questions: QuizQuestion[]) => {
  const languages = new Set<string>();

  addLanguages(languages, quiz.title);
  addLanguages(languages, quiz.description);
  quiz.quiz.chapters?.forEach((chapter) => {
    addLanguages(languages, chapter.title);
    addLanguages(languages, chapter.description);
  });
  questions.forEach((question) => {
    addLanguages(languages, question.text);
    question.answers?.forEach((answer) => addLanguages(languages, answer.text));
  });
  quiz.interpretations?.forEach((interpretation) => {
    addLanguages(languages, interpretation.title);
    interpretation.interpretation.forEach((band) =>
      addLanguages(languages, band.text),
    );
  });

  return Array.from(languages);
};

const getLocalizedText = (
  value: LocalizedText[] | undefined,
  selectedLanguage: string,
  fallback: string,
) =>
  value?.find(
    (item) => item.language === selectedLanguage && item.text.trim().length > 0,
  )?.text ??
  value?.find((item) => item.text.trim().length > 0)?.text ??
  fallback;

const questionTypeLabels: Record<QuizQuestionType, string> = {
  [QuizQuestionType.SINGLE]: "Single choice",
  [QuizQuestionType.MULTIPLE]: "Multiple choice",
  [QuizQuestionType.RANGE]: "Numeric range",
  [QuizQuestionType.TEXT]: "Text",
};

const getRangePreview = (range: { from?: number; to?: number } | undefined) => {
  if (range?.from === undefined && range?.to === undefined) return "Any result";
  if (range.from === undefined) return `Up to ${range.to}`;
  if (range.to === undefined) return `${range.from} or higher`;
  if (range.from === range.to) return String(range.from);
  return `${range.from} to ${range.to}`;
};

const getQuestionScoreSummary = (question: QuizQuestion) => {
  if (question.type === QuizQuestionType.TEXT) {
    return `Participation credit · ${question.points ?? 0} points`;
  }
  if (question.type === QuizQuestionType.MULTIPLE) {
    return "Signed answer values";
  }

  const points = question.points ?? 0;
  return `${points} point${points === 1 ? "" : "s"}`;
};

const getScoreQuestionMaximum = (question: QuizQuestion) => {
  if (question.type === QuizQuestionType.MULTIPLE) {
    return question.answers.reduce(
      (total, answer) =>
        answer.isCorrect &&
        answer.staticPoints !== undefined &&
        answer.staticPoints > 0
          ? total + answer.staticPoints
          : total,
      0,
    );
  }

  return question.points ?? 0;
};

const SystemQuizView = ({
  quiz,
  selectedLanguage,
  onLanguageChange,
  onBack,
  onEdit,
  onDelete,
}: SystemQuizViewProps) => {
  if (!hasQuizChapters(quiz.quiz)) {
    return <div role="alert">Unable to display this Quiz: expected quiz.chapters. Check the backend migration.</div>;
  }
  const questions = getQuizQuestions(quiz.quiz);
  const languages = getQuizLanguages(quiz, questions);
  const title = getLocalizedText(
    quiz.title,
    selectedLanguage,
    "Untitled System Quiz",
  );
  const description = getLocalizedText(
    quiz.description,
    selectedLanguage,
    "",
  );
  const maximumScore = questions.reduce(
    (total, question) => total + getScoreQuestionMaximum(question),
    0,
  );
  const isFeedbackTemplate = isFeedbackQuizType(quiz.type ?? "");
  const isScoreEvaluation = quiz.evaluationType === QuizEvaluationType.SCORE;
  const isInterpretationEvaluation =
    quiz.evaluationType === QuizEvaluationType.INTERPRETATION;
  const isResponseOnlyEvaluation =
    quiz.evaluationType === QuizEvaluationType.RESPONSE_ONLY;

  return (
    <div className={s.viewer}>
      <header className={s.viewerHeader}>
        <div>
          <button type="button" className={s.linkButton} onClick={onBack}>
            Back to System Quizzes
          </button>
          <h2 className={s.viewerTitle}>{title}</h2>
          <p className={s.viewerSubtitle}>
            {quiz.type ? quizTypeLabels[quiz.type] : "No System Quiz Type"} ·{" "}
            {evaluationTypeLabels[quiz.evaluationType]} · {questions.length}{" "}
            question{questions.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className={s.viewerActions}>
          <button type="button" className={s.secondaryButton} onClick={onEdit}>
            Update
          </button>
          <button type="button" className={s.dangerButton} onClick={onDelete}>
            Delete
          </button>
        </div>
      </header>

      {languages.length > 0 && (
        <div>
          <div className={s.languageTabs} aria-label="Quiz content language">
            {languages.map((language) => (
              <button
                key={language}
                type="button"
                className={`${s.languageButton} ${
                  selectedLanguage === language ? s.activeLanguageButton : ""
                }`}
                aria-pressed={selectedLanguage === language}
                onClick={() => onLanguageChange(language)}
              >
                {ISO639Lang[language] ?? language}
              </button>
            ))}
          </div>
          <p className={s.languageFallbackHint}>
            Missing translations use the first available non-empty value.
          </p>
        </div>
      )}

      <section className={s.viewerSection}>
        <div>
          <h3 className={s.viewerSectionTitle}>Quiz configuration</h3>
          <p className={s.viewerSectionHint}>
            Product classification and evaluation behavior are independent.
          </p>
        </div>
        <div className={s.settingsGrid}>
          <div className={s.settingItem}>
            <span>System Quiz Type</span>
            <strong>{quiz.type ? quizTypeLabels[quiz.type] : "Not set"}</strong>
          </div>
          <div className={s.settingItem}>
            <span>Evaluation Type</span>
            <strong>{evaluationTypeLabels[quiz.evaluationType]}</strong>
          </div>
          {!isFeedbackTemplate && (
            <div className={s.settingItem}>
              <span>Allowed Retakes</span>
              <strong>{getRetakeSummary(String(quiz.quiz.retakeAmount))}</strong>
            </div>
          )}
          <div className={s.settingItem}>
            <span>Content Languages</span>
            <strong>
              {languages
                .map((language) => ISO639Lang[language] ?? language)
                .join(", ") || "Not set"}
            </strong>
          </div>
          {isScoreEvaluation && (
            <>
              <div className={s.settingItem}>
                <span>Minimum Passing Score</span>
                <strong>{quiz.quiz.minimumPoints ?? "Not set"}</strong>
              </div>
              <div className={s.settingItem}>
                <span>Maximum Available Score</span>
                <strong>{maximumScore}</strong>
              </div>
            </>
          )}
          {isResponseOnlyEvaluation && (
            <div className={s.settingItem}>
              <span>Collection Behavior</span>
              <strong>Responses collected without scoring or interpretation</strong>
            </div>
          )}
        </div>
        {description && <p className={s.viewerDescription}>{description}</p>}
      </section>

      {!isFeedbackTemplate && (
        <section className={s.viewerSection}>
        <div>
          <h3 className={s.viewerSectionTitle}>Project clone defaults</h3>
          <p className={s.viewerSectionHint}>
            These values are copied when this System Quiz is added to a project.
            Changing them later does not update existing Project Quiz clones.
          </p>
        </div>
        <div className={s.cloneDefaultsGrid}>
          <div className={s.cloneDefaultItem}>
            <span>Answer confidentiality</span>
            <strong>
              {quiz.isConfidential ? "Confidential by default" : "Visible by default"}
            </strong>
            <p>
              {quiz.isConfidential
                ? "Project managers cannot view submitted raw answers."
                : "Project managers can view submitted raw answers."}
            </p>
          </div>
          <div className={s.cloneDefaultItem}>
            <span>Project manager editing</span>
            <strong>{quiz.isEditable ? "Allowed by default" : "Disabled by default"}</strong>
            <p>
              {quiz.isEditable
                ? "Project managers and company owners may edit the clone."
                : "Only platform administrators may edit the quiz definition."}
            </p>
          </div>
          <div className={s.cloneDefaultItem}>
            <span>Participant score visibility</span>
            <strong>{quiz.isScoreShown ?? true ? "Shown by default" : "Hidden by default"}</strong>
            <p>
              {quiz.isScoreShown ?? true
                ? "Participants can see their score after submitting the quiz."
                : "Participants do not see their score after submitting the quiz."}
            </p>
          </div>
        </div>
        </section>
      )}

      <section className={s.viewerSection}>
        <h3 className={s.viewerSectionTitle}>Questions</h3>
        <div className={s.questionViewList}>
          {quiz.quiz.chapters.map((chapter, chapterIndex) => {
            const title = getLocalizedText(chapter.title, selectedLanguage, "").trim();
            const description = getLocalizedText(chapter.description, selectedLanguage, "").trim();
            if (!title && !description && !chapter.questions.length) return null;
            const questionOffset = quiz.quiz.chapters.slice(0, chapterIndex)
              .reduce((count, previous) => count + previous.questions.length, 0);
            return (
              <section className={s.questionViewList} key={chapter._id ?? chapterIndex}>
                {title && <h3 className={s.viewerSectionTitle}>{title}</h3>}
                {description && <p className={s.viewerSectionHint}>{description}</p>}
                {chapter.questions.map((question, index) => {
                  const questionIndex = questionOffset + index;
                  return (
                    <article
                      className={s.questionViewCard}
                      key={question._id ?? questionIndex}
                    >
                      <div className={s.questionViewHeader}>
                        <span className={s.questionIndex}>Q{questionIndex + 1}</span>
                        <div>
                          <h4>
                            {getLocalizedText(
                              question.text,
                              selectedLanguage,
                              "Untitled question",
                            )}
                          </h4>
                          <p>
                            {questionTypeLabels[question.type]}
                            {quiz.evaluationType === QuizEvaluationType.SCORE
                              ? ` · ${getQuestionScoreSummary(question)}`
                              : isInterpretationEvaluation && question.type !== QuizQuestionType.TEXT
                                ? " · Values feed interpretation formulas"
                                : ""}
                          </p>
                        </div>
                      </div>

                      <div className={s.answerViewList}>
                        {question.type === QuizQuestionType.TEXT ? (
                          <div className={s.rangeViewRow}>
                            <strong>Response format</strong>
                            <span>Open text response</span>
                            <span>
                              {isScoreEvaluation
                                ? "Any valid non-blank response earns all configured points as participation credit. Responses are not evaluated for correctness."
                                : "Qualitative response; not used in interpretation formulas."}
                              {" "}Respondents must enter 1–5,000 characters after trimming.
                            </span>
                          </div>
                        ) : question.type === QuizQuestionType.RANGE ? (
                          <>
                            <div className={s.rangeViewRow}>
                              <strong>Allowed range</strong>
                              <span>{getRangePreview(question.range)}</span>
                            </div>
                            {isScoreEvaluation && (
                              <div className={s.rangeViewRow}>
                                <strong>Correct range</strong>
                                <span>{getRangePreview(question.correctRange)}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          (question.answers ?? []).map((answer, answerIndex) => (
                            <div
                              className={s.answerViewRow}
                              key={answer._id ?? answerIndex}
                              role="group"
                              aria-label={`Q${questionIndex + 1} Answer ${answerIndex + 1}`}
                            >
                              {isScoreEvaluation ? (
                                <span
                                  className={s.correctnessIcon}
                                  aria-label={
                                    answer.isCorrect ? "Correct answer" : "Incorrect answer"
                                  }
                                  title={
                                    answer.isCorrect ? "Correct answer" : "Incorrect answer"
                                  }
                                >
                                  {answer.isCorrect ? (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  ) : (
                                    <RadioButtonUncheckedIcon
                                      sx={{ color: "#8f8f8f" }}
                                      fontSize="small"
                                    />
                                  )}
                                </span>
                              ) : (
                                <span className={s.answerPosition}>{answerIndex + 1}</span>
                              )}
                              <span>
                                {getLocalizedText(
                                  answer.text,
                                  selectedLanguage,
                                  `Answer ${answerIndex + 1}`,
                                )}
                                {answer.riskFlag === true && (
                                  <>
                                    {" "}
                                    <Chip
                                      component="span"
                                      icon={<FlagOutlinedIcon />}
                                      label="Risk flag"
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                    />
                                  </>
                                )}
                              </span>
                              {(isInterpretationEvaluation ||
                                (isScoreEvaluation &&
                                  question.type === QuizQuestionType.MULTIPLE)) &&
                                answer.staticPoints !== undefined && (
                                  <strong>Static points: {answer.staticPoints}</strong>
                                )}
                            </div>
                          ))
                        )}
                      </div>
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      </section>

      {quiz.evaluationType === QuizEvaluationType.INTERPRETATION &&
        Boolean(quiz.interpretations?.length) && (
          <section className={s.viewerSection}>
            <div>
              <h3 className={s.viewerSectionTitle}>Interpretation dimensions</h3>
              <p className={s.viewerSectionHint}>
                Formulas use the current positional Q1, Q2, and similar labels.
              </p>
            </div>
            <div className={s.questionViewList}>
              {quiz.interpretations?.map((interpretation, index) => (
                <article className={s.questionViewCard} key={index}>
                  <div className={s.questionViewHeader}>
                    <span className={s.dimensionIndex}>D{index + 1}</span>
                    <div>
                      <h4>
                        {getLocalizedText(
                          interpretation.title,
                          selectedLanguage,
                          "Untitled interpretation",
                        )}
                      </h4>
                      <div className={s.formulaRow}>
                        <span>Formula</span>
                        <code className={s.formulaView}>
                          {interpretation.formula}
                        </code>
                      </div>
                    </div>
                  </div>
                  <div className={s.bandViewList}>
                    {interpretation.interpretation.map((band, bandIndex) => (
                      <div className={s.bandViewRow} key={bandIndex}>
                        <div>
                          <span>Result range</span>
                          <strong>{getRangePreview(band.range)}</strong>
                        </div>
                        <p>
                          {getLocalizedText(
                            band.text,
                            selectedLanguage,
                            `Result band ${bandIndex + 1}`,
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
    </div>
  );
};

export default SystemQuizView;
